import { useEffect, useRef, useState } from 'react';
import BrandLogo from '../BrandLogo/BrandLogo';
import CardGridLayoutControl from '../CardGridLayoutControl/CardGridLayoutControl';
import { useFilterBridge } from '../../context/FilterBridgeContext';
import { useTheme } from '../../theme/ThemeContext';
import DateFilterDropdown from './DateFilterDropdown';
import DashboardHeaderSettings from './DashboardHeaderSettings';
import './Menu.css';

const PAGES = [
  // WH/STP paused — uncomment lines below + restore App.js default page to re-enable
  // { id: 'wh', label: 'WH' },
  // { id: 'stp', label: 'STP' },
  { id: 'mt5', label: 'MT5' },
  { id: 'mt4', label: 'MT4' },
];

function SunIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2" />
      <path
        d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LayoutCustomizeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M3 10h18M3 15h18M9 4v16M15 4v16" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function profileInitials(email) {
  const local = String(email || '').split('@')[0] || '';
  const parts = local.split(/[._\-\s]+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }
  return (local.slice(0, 2) || '?').toUpperCase();
}

function ProfileMenu({ email, onLogout }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const initials = profileInitials(email);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="menu__profile" ref={rootRef}>
      <button
        type="button"
        className={`menu__profile-btn${open ? ' menu__profile-btn--open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label="Open profile menu"
        aria-expanded={open}
        aria-haspopup="true"
        title={email || 'Profile'}
      >
        <span className="menu__profile-avatar" aria-hidden="true">
          {initials}
        </span>
      </button>

      {open ? (
        <div className="menu__profile-panel" role="menu" aria-label="Profile">
          {email ? (
            <div className="menu__profile-email" title={email}>
              {email}
            </div>
          ) : null}
          {onLogout ? (
            <button
              type="button"
              className="menu__profile-logout"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onLogout();
              }}
            >
              Log out
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function Menu({
  activePage,
  onPageChange,
  userEmail,
  isAdmin,
  onLogout,
  onOpenCreateUser,
  onOpenTotp,
}) {
  const { theme, toggleTheme } = useTheme();
  const { filters } = useFilterBridge();
  const [layoutCustomizeOpen, setLayoutCustomizeOpen] = useState(false);

  return (
    <header className="menu">
      <div className="menu__top">
        <div className="menu__left">
          <div className="menu__brand">
            <BrandLogo />
          </div>

          <nav className="menu__nav" aria-label="Dashboard navigation">
            <div className="menu__nav-pill" role="tablist">
              {PAGES.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  role="tab"
                  aria-selected={activePage === p.id}
                  className={`menu__nav-btn${activePage === p.id ? ' menu__nav-btn--active' : ''}`}
                  onClick={() => onPageChange(p.id)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </nav>

          {filters?.extras ? <div className="menu__page-tools">{filters.extras}</div> : null}
        </div>

        <div className="menu__actions">
          <button
            type="button"
            className={`menu__icon-btn menu__layout-btn${layoutCustomizeOpen ? ' menu__icon-btn--active' : ''}`}
            onClick={() => setLayoutCustomizeOpen((open) => !open)}
            aria-label={layoutCustomizeOpen ? 'Hide layout controls' : 'Show layout controls'}
            aria-pressed={layoutCustomizeOpen}
            title={layoutCustomizeOpen ? 'Hide layout' : 'Customize layout'}
          >
            <LayoutCustomizeIcon />
          </button>

          {layoutCustomizeOpen ? <CardGridLayoutControl /> : null}

          <DashboardHeaderSettings
            isAdmin={isAdmin}
            onOpenCreateUser={onOpenCreateUser}
            onOpenTotp={onOpenTotp}
          />

          <button
            type="button"
            className="menu__icon-btn menu__theme-btn"
            onClick={toggleTheme}
            aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            title={theme === 'light' ? 'Dark mode' : 'Light mode'}
          >
            {theme === 'light' ? <MoonIcon /> : <SunIcon />}
          </button>

          {(userEmail || onLogout) ? (
            <ProfileMenu email={userEmail} onLogout={onLogout} />
          ) : null}

          <div className="menu__status-pill" role="status">
            <span className="menu__status-label">API Status</span>
            <span className="menu__status-dot" aria-hidden="true" />
            <span className="menu__status-text">Connected</span>
          </div>

          <DateFilterDropdown activePage={activePage} />
        </div>
      </div>
    </header>
  );
}
